import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clearAdminPassword } from '../utils/api';

const NAV_ITEMS = [
  { path: '/products', label: 'Products', icon: ProductsIcon },
  { path: '/products/import', label: 'Import', icon: ImportIcon },
  { path: '/teams', label: 'Teams', icon: TeamsIcon },
  { path: '/orders', label: 'Orders', icon: OrdersIcon }
];

export default function Layout({ children, title }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  function isActive(path) {
    if (path === '/products') return location.pathname === '/products';
    return location.pathname.startsWith(path);
  }

  function handleLogout() {
    clearAdminPassword();
    setMenuOpen(false);
    navigate('/');
  }

  return (
    <>
      <style>{css}</style>
      <div className="layout">
        {/* Desktop sidebar */}
        <aside className="sidebar">
          <div className="sidebar-brand">Hockey Sticks</div>
          <nav className="sidebar-nav">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={isActive(item.path) ? 'sidebar-link sidebar-link-active' : 'sidebar-link'}
                >
                  <Icon />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
          <button onClick={handleLogout} className="sidebar-logout">
            <SignOutIcon />
            <span>Sign out</span>
          </button>
        </aside>

        {/* Main content area */}
        <main className="main">
          {/* Mobile top bar with hamburger */}
          <header className="mobile-topbar">
            <button onClick={() => setMenuOpen(true)} className="hamburger" aria-label="Menu">
              <HamburgerIcon />
            </button>
            <div className="mobile-title">{title || 'Hockey Sticks'}</div>
            <div style={{ width: 40 }} />
          </header>

          <div className="content">{children}</div>
        </main>

        {/* Bottom nav (mobile only) */}
        <nav className="bottom-nav">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={isActive(item.path) ? 'bottom-link bottom-link-active' : 'bottom-link'}
              >
                <Icon />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Slide menu (mobile only, opens via hamburger) */}
        {menuOpen && (
          <>
            <div className="slide-backdrop" onClick={() => setMenuOpen(false)} />
            <div className="slide-menu">
              <div className="slide-header">
                <div className="slide-brand">Hockey Sticks</div>
                <button onClick={() => setMenuOpen(false)} className="slide-close" aria-label="Close">×</button>
              </div>
              <nav className="slide-nav">
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMenuOpen(false)}
                      className={isActive(item.path) ? 'slide-link slide-link-active' : 'slide-link'}
                    >
                      <Icon />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
              <button onClick={handleLogout} className="slide-logout">
                <SignOutIcon />
                <span>Sign out</span>
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/* ---------- Icons (inline SVG, no dependencies) ---------- */
function ProductsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}
function ImportIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
function TeamsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function OrdersIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4" />
      <polyline points="9 7 12 4 15 7" />
      <line x1="12" y1="4" x2="12" y2="14" />
    </svg>
  );
}
function SignOutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
function HamburgerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

const css = `
  .layout {
    min-height: 100vh;
    background: #F5F5F7;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    display: flex;
  }

  /* ---------- Desktop sidebar ---------- */
  .sidebar {
    width: 240px;
    background: #fff;
    border-right: 1px solid #f0f0f2;
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
    position: sticky;
    top: 0;
    height: 100vh;
    box-sizing: border-box;
  }
  .sidebar-brand {
    font-size: 17px;
    font-weight: 600;
    color: #1d1d1f;
    padding: 0 12px 24px 12px;
  }
  .sidebar-nav {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }
  .sidebar-link {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    color: #1d1d1f;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
  }
  .sidebar-link:hover { background: #f5f5f7; }
  .sidebar-link-active { background: #0071e3; color: #fff; }
  .sidebar-link-active:hover { background: #0071e3; }
  .sidebar-logout {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: transparent;
    border: none;
    color: #86868b;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    margin-top: auto;
  }
  .sidebar-logout:hover { background: #f5f5f7; color: #1d1d1f; }

  /* ---------- Main content ---------- */
  .main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .content {
    flex: 1;
    padding: 32px 40px;
    box-sizing: border-box;
    width: 100%;
  }

  /* ---------- Mobile top bar (hidden on desktop) ---------- */
  .mobile-topbar { display: none; }

  /* ---------- Bottom nav (hidden on desktop) ---------- */
  .bottom-nav { display: none; }

  /* ---------- Slide menu (hidden on desktop) ---------- */
  .slide-backdrop, .slide-menu { display: none; }

  /* ---------- Mobile breakpoint ---------- */
  @media (max-width: 700px) {
    .layout { display: block; }

    .sidebar { display: none; }

    .main { display: block; }

    .mobile-topbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #fff;
      border-bottom: 1px solid #f0f0f2;
      padding: 12px 16px;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    .hamburger {
      background: transparent;
      border: none;
      padding: 8px;
      color: #1d1d1f;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
    }
    .mobile-title {
      font-size: 16px;
      font-weight: 600;
      color: #1d1d1f;
    }

    .content {
      padding: 20px 16px 100px 16px;
    }

    .bottom-nav {
      display: flex;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: #fff;
      border-top: 1px solid #f0f0f2;
      padding: 8px 0 12px 0;
      z-index: 10;
    }
    .bottom-link {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 6px 4px;
      color: #86868b;
      text-decoration: none;
      font-size: 11px;
      font-weight: 500;
    }
    .bottom-link-active { color: #0071e3; }

    .slide-backdrop {
      display: block;
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.4);
      z-index: 100;
    }
    .slide-menu {
      display: flex;
      flex-direction: column;
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      width: 80%;
      max-width: 320px;
      background: #fff;
      z-index: 101;
      padding: 20px 16px;
      box-shadow: 2px 0 20px rgba(0,0,0,0.1);
      box-sizing: border-box;
    }
    .slide-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding: 0 8px;
    }
    .slide-brand {
      font-size: 17px;
      font-weight: 600;
      color: #1d1d1f;
    }
    .slide-close {
      background: transparent;
      border: none;
      font-size: 28px;
      color: #86868b;
      cursor: pointer;
      width: 36px;
      height: 36px;
      line-height: 1;
      padding: 0;
    }
    .slide-nav {
      display: flex;
      flex-direction: column;
      gap: 4px;
      flex: 1;
    }
    .slide-link {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      border-radius: 10px;
      color: #1d1d1f;
      text-decoration: none;
      font-size: 15px;
      font-weight: 500;
    }
    .slide-link-active { background: #0071e3; color: #fff; }
    .slide-logout {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      border-radius: 10px;
      background: transparent;
      border: none;
      color: #86868b;
      font-size: 15px;
      font-weight: 500;
      cursor: pointer;
      text-align: left;
      font-family: inherit;
      margin-top: auto;
    }
  }
`;
